<script>
import { mapGetters } from 'vuex';
import LoadingState from './components/widgets/LoadingState.vue';
import NetworkNotification from './components/NetworkNotification.vue';
import TopRightProfileMenu from './components-next/TopRightProfileMenu.vue';
import PaymentPendingBanner from './components/app/PaymentPendingBanner.vue';
import PendingEmailVerificationBanner from './components/app/PendingEmailVerificationBanner.vue';
import vueActionCable from './helper/actionCable';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import WootSnackbarBox from './components/SnackbarContainer.vue';
import { setColorTheme } from './helper/themeHelper';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import { useAccount } from 'dashboard/composables/useAccount';
import { useFontSize } from 'dashboard/composables/useFontSize';
import {
  registerSubscription,
  verifyServiceWorkerExistence,
} from './helper/pushHelper';
import ReconnectService from 'dashboard/helper/ReconnectService';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { clearCookiesOnLogout } from 'dashboard/store/utils/api';
import DeployUpdateNotice from './components/app/DeployUpdateNotice.vue';
import AtrasoSeleccionModal from './components/app/AtrasoSeleccionModal.vue';
import AtrasoResponsableModal from './components/app/AtrasoResponsableModal.vue';
import ResolveReminderModal from './components/app/ResolveReminderModal.vue';
import NewTaskAlertModal from './components/widgets/conversation/NewTaskAlertModal.vue';
import TaskPriorityReminderModal from './components/widgets/conversation/TaskPriorityReminderModal.vue';
import AnnouncementModal from './components/app/AnnouncementModal.vue';
import AnnouncementAdminCounter from './components/app/AnnouncementAdminCounter.vue';
import PorFacturarCounter from './components/app/PorFacturarCounter.vue';
import WorkStartModal from './components/app/WorkStartModal.vue';

export default {
  name: 'App',

  components: {
    LoadingState,
    NetworkNotification,
    PaymentPendingBanner,
    WootSnackbarBox,
    PendingEmailVerificationBanner,
    TopRightProfileMenu,
    DeployUpdateNotice,
    AtrasoSeleccionModal,
    AtrasoResponsableModal,
    ResolveReminderModal,
    NewTaskAlertModal,
    TaskPriorityReminderModal,
    AnnouncementModal,
    AnnouncementAdminCounter,
    PorFacturarCounter,
    WorkStartModal,
  },
  setup() {
    const router = useRouter();
    const store = useStore();
    const { accountId } = useAccount();
    // Use the font size composable (it automatically sets up the watcher)
    const { currentFontSize } = useFontSize();
    const { uiSettings } = useUISettings();

    return {
      router,
      store,
      currentAccountId: accountId,
      currentFontSize,
      uiSettings,
    };
  },
  data() {
    return {
      reconnectService: null,
      deployVersionTimer: null,
      bootGitSha: null,
      onVisibilityChange: null,
      deployUpdateDetected: false,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      isRTL: 'accounts/isRTL',
      currentUser: 'getCurrentUser',
      authUIFlags: 'getAuthUIFlags',
      accountUIFlags: 'accounts/getUIFlags',
      selectedChat: 'getSelectedChat',
    }),
    // Código de versión (Victor 2026-05-26): sube +1 en cada deploy a prod.
    // Empieza en 500. Se muestra fijo abajo-derecha en todo el CRM.
    deployCount() {
      const n = Number(this.$store.getters['globalConfig/get']?.deployCount);
      return Number.isFinite(n) && n > 0 ? n : null;
    },
    // Con un chat abierto, el ⚙ va anclado DENTRO de la cabecera (junto a
    // Resolver) vía ConversationHeader. Acá ocultamos el flotante global para
    // que NO haya dos ⚙ (Victor 2026-05-18).
    isConversationOpen() {
      return !!this.selectedChat?.id;
    },
    hideOnOnboardingView() {
      return !isOnOnboardingView(this.$route);
    },
    // Oculta el TopRightProfileMenu (gear ⚙ flotante con Mi perfil/Apariencia/etc.)
    // cuando estamos en el módulo de Leads/Pipelines · pedido por Victor 2026-05-13
    isInPipelinesView() {
      const path = this.$route?.fullPath || this.$route?.path || '';
      return path.includes('/pipelines');
    },
  },

  watch: {
    currentAccountId: {
      immediate: true,
      handler() {
        if (this.currentAccountId) {
          this.initializeAccount();
        }
      },
    },
  },
  mounted() {
    this.initializeColorTheme();
    this.listenToThemeChanges();
    // If user locale is set, use it; otherwise use account locale
    this.setLocale(
      this.uiSettings?.locale || window.chatwootConfig.selectedLocale
    );
  },
  unmounted() {
    if (this.reconnectService) {
      this.reconnectService.disconnect();
    }
    if (this.deployVersionTimer) {
      clearInterval(this.deployVersionTimer);
    }
    if (this.onVisibilityChange) {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
  },
  methods: {
    initializeColorTheme() {
      setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
    },
    listenToThemeChanges() {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.onchange = e => setColorTheme(e.matches);
    },
    setLocale(locale) {
      this.$root.$i18n.locale = locale;
    },
    async initializeAccount() {
      await this.$store.dispatch('accounts/get');
      this.$store.dispatch('setActiveAccount', {
        accountId: this.currentAccountId,
      });
      // Pre-cargar labels y teams · disponibles globalmente para todos los roles
      this.$store.dispatch('labels/get');
      this.$store.dispatch('teams/get');
      const { locale } = this.getAccount(this.currentAccountId);
      const { pubsub_token: pubsubToken } = this.currentUser || {};
      // If user locale is set, use it; otherwise use account locale
      this.setLocale(this.uiSettings?.locale || locale);
      vueActionCable.init(this.store, pubsubToken);
      this.reconnectService = new ReconnectService(this.store, this.router);
      window.reconnectService = this.reconnectService;

      verifyServiceWorkerExistence(registration =>
        registration.pushManager.getSubscription().then(subscription => {
          if (subscription) {
            registerSubscription();
          }
        })
      );

      this.startDeployVersionWatcher();
    },
    // Tras cada deploy en prod, cuando el contenedor nuevo queda arriba, su
    // /deploy_version devuelve un sha distinto al que cargó esta pantalla. Al
    // detectarlo forzamos logout → login, así el usuario recarga el frontend
    // nuevo y se limpia la caché. Durante el build el contenedor viejo sigue
    // sirviendo el sha viejo, por lo que nadie se desconecta mientras trabaja.
    startDeployVersionWatcher() {
      if (this.deployVersionTimer) return;
      this.bootGitSha = window.globalConfig?.GIT_SHA;
      if (!this.bootGitSha) return; // sin sha de referencia no hay nada que comparar
      this.onVisibilityChange = () => {
        if (document.visibilityState === 'visible') this.checkDeployVersion();
      };
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.deployVersionTimer = setInterval(this.checkDeployVersion, 60000);
    },
    async checkDeployVersion() {
      if (this.deployUpdateDetected) return;
      try {
        const res = await fetch('/deploy_version', { cache: 'no-store' });
        if (!res.ok) return;
        const { sha } = await res.json();
        if (sha && this.bootGitSha && sha !== this.bootGitSha) {
          // Versión nueva detectada: mostramos el aviso + cuenta regresiva
          // (en vez de cerrar de una). Dejamos de chequear para no re-disparar.
          this.deployUpdateDetected = true;
          if (this.deployVersionTimer) clearInterval(this.deployVersionTimer);
        }
      } catch (_) {
        /* sin red / error transitorio · reintenta en el próximo ciclo */
      }
    },
    // Al terminar la cuenta regresiva: cierra sesión → login → recarga versión nueva.
    onDeployExpired() {
      clearCookiesOnLogout();
    },
  },
};
</script>

<template>
  <div
    v-if="!authUIFlags.isFetching && !accountUIFlags.isFetchingItem"
    id="app"
    class="flex flex-col w-full h-screen min-h-0 bg-n-background"
    :dir="isRTL ? 'rtl' : 'ltr'"
  >
    <!-- Banner azul de plantillas oculto por pedido de Victor 2026-06-02 (PC + celular). -->
    <!-- <UpdateBanner /> -->
    <template v-if="currentAccountId">
      <PendingEmailVerificationBanner v-if="hideOnOnboardingView" />
      <PaymentPendingBanner v-if="hideOnOnboardingView" />
    </template>
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
    <TopRightProfileMenu v-if="currentUser && currentAccountId && !isInPipelinesView && !isConversationOpen" />
    <WootSnackbarBox />
    <NetworkNotification />
    <DeployUpdateNotice
      v-if="deployUpdateDetected"
      @expired="onDeployExpired"
    />
    <AtrasoSeleccionModal v-if="currentUser && currentAccountId" />
    <AtrasoResponsableModal v-if="currentUser && currentAccountId" />
    <ResolveReminderModal v-if="currentUser && currentAccountId" />
    <NewTaskAlertModal v-if="currentUser && currentAccountId" />
    <TaskPriorityReminderModal v-if="currentUser && currentAccountId" />
    <!-- Aviso obligatorio "Un solo equipo" (Victor 2026-06-07): modal bloqueante
         para agentes + contador/desglose solo para super_admin. -->
    <AnnouncementModal v-if="currentUser && currentAccountId" />
    <AnnouncementAdminCounter v-if="currentUser && currentAccountId" />
    <!-- Contador "Por Facturar" (Victor 2026-06-09): pagos detectados HOY ·
         badge 🧾 visible solo para 3 cuentas (el backend filtra). -->
    <PorFacturarCounter v-if="currentUser && currentAccountId" />
    <!-- Hora de inicio de jornada · monitoreo (Victor 2026-06-09): modal bloqueante
         que registra y muestra la hora de entrada del agente, 1 vez al día. -->
    <WorkStartModal v-if="currentUser && currentAccountId" />
    <!-- Código de versión · fijo abajo-derecha · sube cada actualización -->
    <div
      v-if="currentUser && currentAccountId && deployCount"
      class="version-badge"
      title="Versión del CRM · sube en cada actualización"
    >
      <span class="vb-dot"></span>
      <span class="vb-tx">v{{ deployCount }}</span>
    </div>
  </div>
  <LoadingState v-else />
</template>

<style lang="scss">
@import './assets/scss/app';

.v-popper--theme-tooltip .v-popper__inner {
  background: black !important;
  font-size: 0.75rem;
  padding: 4px 8px !important;
  border-radius: 6px;
  font-weight: 400;
}

.v-popper--theme-tooltip .v-popper__arrow-container {
  display: none;
}

/* Código de versión · fijo abajo-derecha · sin recuadro, discreto (Victor
   2026-05-26): solo punto verde mini + texto dorado pequeño, sin caja. */
.version-badge {
  position: fixed;
  bottom: 12px;
  right: 14px;
  z-index: 60;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  pointer-events: none;
  user-select: none;
}
.version-badge .vb-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: #3ddc84; box-shadow: 0 0 6px #3ddc84; flex-shrink: 0;
}
.version-badge .vb-tx {
  font-size: 11px; font-weight: 700; letter-spacing: 0.04em;
  color: rgba(201, 163, 90, 0.75);
}
</style>
